import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiConfig } from "../config/api.config";

function formatCOP(v) {
  if (v === null || v === undefined || isNaN(v) || v === "") return "$ 0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parseFloat(v));
}

function formatPct(v) {
  if (v === null || v === undefined) return "—";
  return parseFloat(v).toFixed(2) + "%";
}

function formatPeriodo(p) {
  if (!p) return "";
  const [y, m] = p.split("-");
  const meses = [
    "",
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  return `${meses[parseInt(m, 10)]} ${y}`;
}

function getCategoria(ccpet) {
  const c = String(ccpet || "");
  if (c.startsWith("2.1")) return "Funcionamiento";
  if (c.startsWith("2.2")) return "Servicio de la deuda";
  if (c.startsWith("2.3")) return "Inversión";
  return "—";
}

const th = {
  padding: "0.5rem 0.5rem",
  background: "#dbeafe",
  color: "#1e40af",
  fontWeight: 700,
  fontSize: "0.78rem",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const thL = { ...th, textAlign: "left" };

const td = {
  padding: "0.45rem 0.5rem",
  fontSize: "0.8rem",
  textAlign: "right",
  borderBottom: "1px solid #e5e7eb",
};

const tdL = { ...td, textAlign: "left" };

const tot = {
  ...td,
  fontWeight: 700,
  background: "#eff6ff",
  color: "#163b8c",
};

const totL = { ...tot, textAlign: "left" };

const selectStyle = {
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  padding: "0.4rem 0.65rem",
  fontSize: "0.85rem",
};

function AccordionBlock({ title, resumen, children }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "16px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
        marginBottom: "1.5rem",
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: "1.1rem 1.5rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: open ? "#eff6ff" : "#fff",
          borderBottom: open ? "1px solid #dbeafe" : "none",
          cursor: "pointer",
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              color: "#163b8c",
              fontSize: "1.05rem",
              fontWeight: 700,
            }}
          >
            {title}
          </h2>

          {resumen && (
            <div
              style={{
                marginTop: "0.35rem",
                display: "flex",
                gap: "1.25rem",
                flexWrap: "wrap",
              }}
            >
              {resumen.map((item, i) => (
                <span key={i} style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                  {item.label}:{" "}
                  <strong style={{ color: "#163b8c" }}>{item.value}</strong>
                </span>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
          style={{
            background: open ? "#dbeafe" : "#f0f4ff",
            color: "#1d4ed8",
            border: "1px solid #c7d2fe",
            borderRadius: "8px",
            padding: "0.35rem 0.8rem",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.82rem",
            whiteSpace: "nowrap",
            marginLeft: "1rem",
          }}
        >
          {open ? "Ocultar ▲" : "Ver detalle ▼"}
        </button>
      </div>

      {open && <div style={{ padding: "1rem 1.25rem" }}>{children}</div>}
    </div>
  );
}

export default function SeguimientoPresupuestal() {
  const navigate = useNavigate();

  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [fuenteIng, setFuenteIng] = useState("TODAS");
  const [fuenteEgr, setFuenteEgr] = useState("TODAS");
  const [seccionEgr, setSeccionEgr] = useState("TODAS");
  const [categoriaEgr, setCategoriaEgr] = useState("TODAS");

  useEffect(() => {
    fetch(apiConfig.getUrl("/api/hacienda/seguimiento-presupuestal/"), {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.error);
        setDatos(d);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ padding: "3rem", textAlign: "center", color: "#6b7280" }}>
        Cargando...
      </div>
    );
  }

  const ingFiltrados =
    datos?.ingresos?.filter(
      (r) => fuenteIng === "TODAS" || r.fuente_financiacion === fuenteIng
    ) || [];

  const egrFiltrados =
    datos?.egresos?.filter((r) => {
      const categoria = getCategoria(r.ccpet);

      return (
        (fuenteEgr === "TODAS" || r.fuente_financiacion === fuenteEgr) &&
        (seccionEgr === "TODAS" ||
          String(r.seccion_presupuestal) === seccionEgr) &&
        (categoriaEgr === "TODAS" || categoria === categoriaEgr)
      );
    }) || [];

  const totIng = {
    ppto: ingFiltrados.reduce(
      (s, r) => s + parseFloat(r.presupuesto_definitivo || 0),
      0
    ),
    rec: ingFiltrados.reduce(
      (s, r) => s + parseFloat(r.total_recaudo || 0),
      0
    ),
  };

  const totEgr = {
    ppto: egrFiltrados.reduce(
      (s, r) => s + parseFloat(r.ptto_definitivo || 0),
      0
    ),
    comp: egrFiltrados.reduce(
      (s, r) => s + parseFloat(r.compromiso || 0),
      0
    ),
    disp: egrFiltrados.reduce(
      (s, r) => s + parseFloat(r.disponible || 0),
      0
    ),
  };

  const totCon = {
    ppto:
      datos?.consolidado?.reduce(
        (s, r) => s + parseFloat(r.ppto_definitivo || 0),
        0
      ) || 0,
    rec:
      datos?.consolidado?.reduce(
        (s, r) => s + parseFloat(r.recaudo || 0),
        0
      ) || 0,
    comp:
      datos?.consolidado?.reduce(
        (s, r) => s + parseFloat(r.compromisos || 0),
        0
      ) || 0,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f3f4f6",
        padding: "2rem 1.5rem",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <button
          type="button"
          onClick={() => navigate("/hacienda-presupuesto")}
          style={{
            background: "#e5e7eb",
            color: "#111827",
            border: "none",
            borderRadius: "10px",
            padding: "0.7rem 1rem",
            cursor: "pointer",
            fontWeight: 600,
            marginBottom: "1.5rem",
          }}
        >
          ← Volver a Hacienda
        </button>

        <div style={{ marginBottom: "1.5rem" }}>
          <h1
            style={{
              margin: 0,
              color: "#163b8c",
              fontSize: "2rem",
              fontWeight: 800,
            }}
          >
            Seguimiento Presupuestal
          </h1>
          <p style={{ marginTop: "0.5rem", color: "#6b7280" }}>
            Periodo: <strong>{formatPeriodo(datos?.periodo)}</strong>
          </p>
        </div>

        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fca5a5",
              borderRadius: "12px",
              padding: "1rem",
              color: "#991b1b",
              marginBottom: "1rem",
            }}
          >
            ⚠️ {error}
          </div>
        )}

        <AccordionBlock
          title="Seguimiento Consolidado"
          resumen={[
            { label: "Ppto Definitivo", value: formatCOP(totCon.ppto) },
            { label: "Recaudo", value: formatCOP(totCon.rec) },
            { label: "Compromisos", value: formatCOP(totCon.comp) },
            {
              label: "% Ejecución",
              value:
                totCon.ppto > 0
                  ? (totCon.rec / totCon.ppto * 100).toFixed(2) + "%"
                  : "—",
            },
          ]}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thL}>FUENTE</th>
                  <th style={thL}>NOMBRE FUENTE</th>
                  <th style={th}>PPTO DEFINITIVO</th>
                  <th style={th}>RECAUDO</th>
                  <th style={th}>COMPROMISOS</th>
                  <th style={th}>% EJECUCIÓN</th>
                  <th style={th}>DIFERENCIA</th>
                </tr>
              </thead>
              <tbody>
                {datos?.consolidado?.map((r, i) => (
                  <tr
                    key={i}
                    style={{ background: i % 2 === 0 ? "#fff" : "#f8faff" }}
                  >
                    <td style={tdL}>
                      <strong>{r.fuente_financiacion}</strong>
                    </td>
                    <td style={tdL}>{r.nombre_fuente}</td>
                    <td style={td}>{formatCOP(r.ppto_definitivo)}</td>
                    <td style={td}>{formatCOP(r.recaudo)}</td>
                    <td style={td}>{formatCOP(r.compromisos)}</td>
                    <td
                      style={{
                        ...td,
                        color:
                          parseFloat(r.pct_ejecucion) > 100
                            ? "#dc2626"
                            : "#16a34a",
                        fontWeight: 600,
                      }}
                    >
                      {formatPct(r.pct_ejecucion)}
                    </td>
                    <td
                      style={{
                        ...td,
                        color:
                          parseFloat(r.diferencia) < 0 ? "#dc2626" : "#374151",
                      }}
                    >
                      {formatCOP(r.diferencia)}
                    </td>
                  </tr>
                ))}

                <tr>
                  <td style={totL} colSpan={2}>
                    TOTAL
                  </td>
                  <td style={tot}>{formatCOP(totCon.ppto)}</td>
                  <td style={tot}>{formatCOP(totCon.rec)}</td>
                  <td style={tot}>{formatCOP(totCon.comp)}</td>
                  <td style={tot}>
                    {totCon.ppto > 0
                      ? (totCon.rec / totCon.ppto * 100).toFixed(2) + "%"
                      : "—"}
                  </td>
                  <td style={tot}>{formatCOP(totCon.ppto - totCon.rec)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </AccordionBlock>

        <AccordionBlock
          title="Ingresos"
          resumen={[
            { label: "Registros", value: ingFiltrados.length },
            { label: "Ppto Definitivo", value: formatCOP(totIng.ppto) },
            { label: "Recaudo", value: formatCOP(totIng.rec) },
            {
              label: "% Ejecución",
              value:
                totIng.ppto > 0
                  ? (totIng.rec / totIng.ppto * 100).toFixed(2) + "%"
                  : "—",
            },
          ]}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "1rem",
            }}
          >
            <label
              style={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              Fuente:
            </label>
            <select
              value={fuenteIng}
              onChange={(e) => setFuenteIng(e.target.value)}
              style={selectStyle}
            >
              <option value="TODAS">Todas</option>
              {datos?.fuentes_ing?.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thL}>CCPET</th>
                  <th style={thL}>NOMBRE</th>
                  <th style={th}>PPTO DEFINITIVO</th>
                  <th style={th}>RECAUDO</th>
                  <th style={th}>DIFERENCIA</th>
                  <th style={th}>% EJECUCIÓN</th>
                </tr>
              </thead>
              <tbody>
                {ingFiltrados.map((r, i) => {
                  const ppto = parseFloat(r.presupuesto_definitivo || 0);
                  const rec = parseFloat(r.total_recaudo || 0);
                  const dif = ppto - rec;
                  const pct = ppto > 0 ? (rec / ppto) * 100 : null;

                  return (
                    <tr
                      key={i}
                      style={{ background: i % 2 === 0 ? "#fff" : "#f8faff" }}
                    >
                      <td
                        style={{
                          ...tdL,
                          fontFamily: "monospace",
                          fontSize: "0.76rem",
                        }}
                      >
                        {r.ccpet}
                      </td>
                      <td style={tdL}>{r.nombre}</td>
                      <td style={td}>{formatCOP(ppto)}</td>
                      <td style={td}>{formatCOP(rec)}</td>
                      <td
                        style={{
                          ...td,
                          color: dif < 0 ? "#dc2626" : "#374151",
                        }}
                      >
                        {formatCOP(dif)}
                      </td>
                      <td
                        style={{
                          ...td,
                          color:
                            pct !== null && pct > 100
                              ? "#dc2626"
                              : "#16a34a",
                          fontWeight: 600,
                        }}
                      >
                        {pct !== null ? pct.toFixed(2) + "%" : "—"}
                      </td>
                    </tr>
                  );
                })}

                <tr>
                  <td style={totL} colSpan={2}>
                    TOTAL
                  </td>
                  <td style={tot}>{formatCOP(totIng.ppto)}</td>
                  <td style={tot}>{formatCOP(totIng.rec)}</td>
                  <td style={tot}>{formatCOP(totIng.ppto - totIng.rec)}</td>
                  <td style={tot}>
                    {totIng.ppto > 0
                      ? (totIng.rec / totIng.ppto * 100).toFixed(2) + "%"
                      : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </AccordionBlock>

        <AccordionBlock
          title="Egresos"
          resumen={[
            { label: "Registros", value: egrFiltrados.length },
            { label: "Ppto Definitivo", value: formatCOP(totEgr.ppto) },
            { label: "Compromiso", value: formatCOP(totEgr.comp) },
            {
              label: "% Ejecución",
              value:
                totEgr.ppto > 0
                  ? (totEgr.comp / totEgr.ppto * 100).toFixed(2) + "%"
                  : "—",
            },
          ]}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              marginBottom: "1rem",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <label
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                Fuente:
              </label>
             <select
  value={fuenteEgr}
  onChange={(e) => {
    const value = e.target.value;
    setFuenteEgr(value);
    setSeccionEgr("TODAS");
    setCategoriaEgr("TODAS");
  }}
  style={selectStyle}
>
  <option value="TODAS">Todas</option>
  {datos?.fuentes_egr?.map((f) => (
    <option key={f} value={f}>
      {f}
    </option>
  ))}
</select>
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <label
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                 Sección Presupuestal:
              </label>
              <select
                value={seccionEgr}
                onChange={(e) => setSeccionEgr(e.target.value)}
                style={selectStyle}
              >
                <option value="TODAS">Todas</option>
                {datos?.secciones_egr?.map((s) => (
                  <option key={s} value={String(s)}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <label
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "#374151",
                }}
              >
                Categoría:
              </label>
              <select
                value={categoriaEgr}
                onChange={(e) => setCategoriaEgr(e.target.value)}
                style={selectStyle}
              >
                <option value="TODAS">Todas</option>
                <option value="Funcionamiento">Funcionamiento</option>
                <option value="Servicio de la deuda">
                  Servicio de la deuda
                </option>
                <option value="Inversión">Inversión</option>
              </select>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thL}>CCPET</th>
                  <th style={thL}>NOMBRE</th>
                  <th style={th}>PPTO DEFINITIVO</th>
                  <th style={th}>COMPROMISO</th>
                  <th style={th}>% EJECUCIÓN</th>
                  <th style={th}>DISPONIBLE</th>
                </tr>
              </thead>
              <tbody>
                {egrFiltrados.map((r, i) => {
                  const ppto = parseFloat(r.ptto_definitivo || 0);
                  const comp = parseFloat(r.compromiso || 0);
                  const disp = parseFloat(r.disponible || 0);
                  const pct = ppto > 0 ? (comp / ppto) * 100 : null;

                  return (
                    <tr
                      key={i}
                      style={{ background: i % 2 === 0 ? "#fff" : "#f8faff" }}
                    >
                      <td
                        style={{
                          ...tdL,
                          fontFamily: "monospace",
                          fontSize: "0.76rem",
                        }}
                      >
                        {r.ccpet}
                      </td>
                      <td style={tdL}>{r.nombre}</td>
                      <td style={td}>{formatCOP(ppto)}</td>
                      <td style={td}>{formatCOP(comp)}</td>
                      <td
                        style={{
                          ...td,
                          color:
                            pct !== null && pct > 100
                              ? "#dc2626"
                              : "#16a34a",
                          fontWeight: 600,
                        }}
                      >
                        {pct !== null ? pct.toFixed(2) + "%" : "—"}
                      </td>
                      <td style={td}>{formatCOP(disp)}</td>
                    </tr>
                  );
                })}

                <tr>
                  <td style={totL} colSpan={2}>
                    TOTAL
                  </td>
                  <td style={tot}>{formatCOP(totEgr.ppto)}</td>
                  <td style={tot}>{formatCOP(totEgr.comp)}</td>
                  <td style={tot}>
                    {totEgr.ppto > 0
                      ? (totEgr.comp / totEgr.ppto * 100).toFixed(2) + "%"
                      : "—"}
                  </td>
                  <td style={tot}>{formatCOP(totEgr.disp)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </AccordionBlock>
      </div>
    </div>
  );
}